import { Link } from "react-router-dom";
import { useState } from "react";
import { InstagramLogo, FacebookLogo, TiktokLogo, WhatsappLogo, EnvelopeSimple, ArrowRight } from "@phosphor-icons/react";
import Logo from "@/components/brand/Logo";
import { joinWaitlist } from "@/lib/api";
import { toast } from "sonner";

const LINKS = {
  Platform: [
    { to: "/services", label: "Services" },
    { to: "/drivers", label: "Become a Driver" },
    { to: "/pros", label: "Become a Service Pro" },
    { to: "/partners", label: "Partner With Us" },
    { to: "/about", label: "About" },
  ],
  Company: [
    { to: "/press", label: "Press & Investors" },
    { to: "/contact", label: "Contact" },
    { to: "/privacy-policy", label: "Privacy Policy" },
    { to: "/terms-and-conditions", label: "Terms & Conditions" },
    { to: "/refund-policy", label: "Refund Policy" },
    { to: "/delete-account", label: "Delete Account" },
  ],
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await joinWaitlist({ email, role: "customer" });
      toast.success(res.message || "You're on the waitlist.");
      setEmail("");
    } catch (err) {
      toast.error("Could not join waitlist. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative border-t border-ih-line mt-32 bg-ih-ink">
      <div className="absolute inset-x-0 top-0 h-px ih-divider" />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <Logo />
            <p className="text-white/60 text-base leading-relaxed max-w-md">
              IslandHop Technologies LLC is building the Caribbean&apos;s next-generation multi-service
              on-demand platform — covering delivery, handyman, cleaning, tech support and events across
              Trinidad &amp; Tobago and beyond.
            </p>

            <form onSubmit={handleSubmit} className="max-w-md" data-testid="footer-waitlist-form">
              <label className="text-xs uppercase tracking-[0.18em] text-white/40 mb-2 block">
                Launching soon — join the waitlist
              </label>
              <div className="flex items-stretch gap-2">
                <div className="flex-1 flex items-center gap-2 px-3 rounded-full border border-ih-line2 bg-black/40 focus-within:border-ih-electric transition">
                  <EnvelopeSimple size={18} className="text-white/40" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    data-testid="footer-waitlist-email"
                    className="flex-1 bg-transparent py-2.5 outline-none text-sm text-white placeholder:text-white/30"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="footer-waitlist-submit"
                  className="inline-flex items-center gap-1.5 px-5 rounded-full bg-ih-neon text-black text-sm font-semibold hover:scale-[1.02] disabled:opacity-60 transition"
                >
                  {loading ? "..." : "Join"} <ArrowRight weight="bold" size={14} />
                </button>
              </div>
            </form>

            <div className="flex items-center gap-3 pt-2">
              <SocialLink href="https://instagram.com" label="Instagram"><InstagramLogo size={18} weight="bold" /></SocialLink>
              <SocialLink href="https://facebook.com" label="Facebook"><FacebookLogo size={18} weight="bold" /></SocialLink>
              <SocialLink href="https://tiktok.com" label="TikTok"><TiktokLogo size={18} weight="bold" /></SocialLink>
              <SocialLink href="https://wa.me/0000000000" label="WhatsApp"><WhatsappLogo size={18} weight="bold" /></SocialLink>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-10">
            {Object.entries(LINKS).map(([title, items]) => (
              <div key={title}>
                <p className="text-xs uppercase tracking-[0.18em] text-white/40 mb-4">{title}</p>
                <ul className="space-y-2.5">
                  {items.map((item) => (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="text-sm text-white/70 hover:text-white transition"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-white/40 mb-4">Contact</p>
              <ul className="space-y-2.5 text-sm text-white/70">
                <li>support@islandhoptt.com</li>
                <li>partners@islandhoptt.com</li>
                <li>drivers@islandhoptt.com</li>
                <li className="text-white/40">Port of Spain · Trinidad &amp; Tobago</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-ih-line flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} IslandHop Technologies LLC. A U.S.-registered technology company.
          </p>
          <p className="text-xs text-white/40 font-mono">
            <span className="text-ih-neon">●</span> Pre-launch · Trinidad &amp; Tobago
          </p>
        </div>
      </div>
    </footer>
  );
}

const SocialLink = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    data-testid={`social-${label.toLowerCase()}`}
    className="w-9 h-9 rounded-full border border-ih-line2 text-white/70 hover:text-white hover:border-ih-electric flex items-center justify-center transition"
  >
    {children}
  </a>
);
